import React from 'react';
import { Box, Flex } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';

import Button from '@/components/Button';
import FormControl from '@/components/FormControl';
import Input from '@/components/Input';
import Textarea from '@/components/Textarea';
import withProps from '@/components/withProps';
import useForm from '@/hooks/useForm';
import { isEmail, isLength, isMobilePhone, isNotEmpty } from '@/lib/validator';

const FormColumn = withProps({
  width: ['100%', null, 'auto'],
  flex: [null, null, '1'],
  p: '2',
})(FormControl);

const FormFlex = withProps({
  flexWrap: 'wrap',
  m: -2,
  py: '2',
})(Flex);

const Form = ({ data, onSubmit, onCancel }) => {
  const { getValue, getError, handleValueChange, handleSubmit } = useForm(
    data,
    onSubmit,
  );

  return (
    <form data-testid="CONTACT-US-FORM" onSubmit={handleSubmit}>
      <FormFlex>
        <FormColumn label="First name" htmlFor="contact_us_given_names">
          <Input
            id="contact_us_given_names"
            data-testid="FIRST_NAME"
            error={getError('givenNames', { onFormDirty: true })}
            onChange={handleValueChange('givenNames', [
              [isNotEmpty, 'First name is required'],
              [
                isLength,
                'First name should be less than 80 characters',
                { max: 80 },
              ],
            ])}
            value={getValue('givenNames')}
            placeholder="First name"
          />
        </FormColumn>
        <FormColumn label="Last name" htmlFor="contact_us_last_name">
          <Input
            id="contact_us_last_name"
            data-testid="LAST_NAME"
            error={getError('lastName', { onFormDirty: true })}
            onChange={handleValueChange('lastName', [
              [isNotEmpty, 'Last name is required'],
              [
                isLength,
                'Last name should be less than 80 characters',
                { max: 80 },
              ],
            ])}
            value={getValue('lastName')}
            placeholder="Last name"
          />
        </FormColumn>
      </FormFlex>
      <FormFlex>
        <FormColumn label="Email address" htmlFor="contact_us_email_address">
          <Input
            data-testid="EMAIL_ADDRESS"
            error={getError('emailAddress', { onFormDirty: true })}
            id="contact_us_email_address"
            onChange={handleValueChange('emailAddress', [
              isEmail,
              'Please enter a valid email address',
            ])}
            value={getValue('emailAddress')}
            placeholder="Email"
          />
        </FormColumn>
        <FormColumn label="Mobile number" htmlFor="contact_us_phone">
          <Input
            data-testid="PHONE"
            error={getError('phone', { onFormDirty: true })}
            id="contact_us_phone"
            onChange={handleValueChange('phone', [
              isMobilePhone,
              'Please input a valid mobile number',
            ])}
            value={getValue('phone')}
            inputmode="tel"
            pattern="\d*"
            placeholder="Mobile number"
          />
        </FormColumn>
      </FormFlex>
      <FormFlex>
        <FormColumn
          optional
          label="Booking reference"
          htmlFor="contact_us_booking_reference"
        >
          <Input
            data-testid="BOOKING_REFERENCE"
            error={getError('bookingReference', { onFormDirty: true })}
            id="contact_us_booking_reference"
            onChange={handleValueChange('bookingReference', [
              isLength,
              'Please input a valid booking reference',
              { max: 10 },
            ])}
            value={getValue('bookingReference')}
            placeholder="Booking reference"
          />
        </FormColumn>
      </FormFlex>
      <FormFlex>
        <FormColumn label="Your message" htmlFor="contact_us_message">
          <Textarea
            data-testid="MESSAGE"
            error={getError('message', { onFormDirty: true })}
            id="contact_us_message"
            onChange={handleValueChange('message', [
              isNotEmpty,
              'Your message is required',
            ])}
            value={getValue('message')}
            characterLimit={1000}
            placeholder="Your message"
          />
        </FormColumn>
      </FormFlex>
      <Box mt="4">
        <Flex flexDirection={['column', null, 'row-reverse']}>
          <Button data-testid="SEND" fullWidth variant="secondary" dialogue>
            Send
          </Button>
          <Button
            type="button"
            data-testid="CANCEL"
            onClick={onCancel && (() => onCancel())}
            mt={['3', null, '0']}
            mr={[null, null, '3']}
            fullWidth
            variant="supporting"
          >
            Cancel
          </Button>
        </Flex>
      </Box>
    </form>
  );
};

Form.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  data: PropTypes.shape({
    givenNames: PropTypes.string,
    lastName: PropTypes.string,
    emailAddress: PropTypes.string,
    phone: PropTypes.string,
    bookingReference: PropTypes.string,
    message: PropTypes.string,
  }),
};

export default Form;
